'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { ReactNode, Ref } from 'react';
import { useState } from 'react';
import { MobileSearchOverlay, RecentSearchChips, SearchBar, useHideOnScroll } from '@/app/_components/SearchBar';
import type { SearchScope } from '@/app/_components/SearchBar/constants';
import { BREAKPOINTS } from '@/constants';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
interface SearchPageHeaderProps {
  keyword: string;
  filterCount: number;
  recentKeywords: string[];
  scope?: SearchScope;
  onSearch: (keyword: string) => void;
  onFilterClick?: () => void;
  onSelectRecent: (keyword: string) => void;
  onRemoveRecent: (keyword: string) => void;
  onClearRecent: () => void;
  //* 노출 판정은 소비처 몫 — 자리 예약 높이가 그 판정에 묶여 있어 여기서 다시 계산하면 두 값이 갈린다.
  trendKeyword?: ReactNode;
  ref?: Ref<HTMLDivElement>;
}

/**
 * 모바일: `position: fixed` 오버레이로 상단 고정 + 스크롤 방향에 따라 translate 숨김.
 * PC: 일반 flow. spacer는 mobile-only.
 * ref는 외부 wrapper에 연결되어 IntersectionObserver의 관찰 대상이 된다
 * (모바일에서는 spacer 높이 = in-flow 기준으로 가시성을 판단).
 */
export const SearchPageHeader = t0 => {
  const $ = _c(46);
  const {
    keyword,
    filterCount,
    recentKeywords,
    scope,
    onSearch,
    onFilterClick,
    onSelectRecent,
    onRemoveRecent,
    onClearRecent,
    trendKeyword,
    ref
  } = t0;
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchLayerOpen, setIsSearchLayerOpen] = useState(false);
  const isMobileTabletViewport = useMediaQuery(BREAKPOINTS.MOBILE_TABLET);
  const {
    hidden
  } = useHideOnScroll();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setIsSearchLayerOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeSearchLayer = t1;
  const isSearchLayerVisible = isSearchLayerOpen && isMobileTabletViewport;
  const {
    release: releaseSearchLayerHistory
  } = useOverlayHistory(isSearchLayerVisible, closeSearchLayer);
  let t2;
  if ($[1] !== releaseSearchLayerHistory) {
    t2 = handler => keyword_0 => {
      releaseSearchLayerHistory();
      handler(keyword_0);
      setIsSearchLayerOpen(false);
    };
    $[1] = releaseSearchLayerHistory;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const searchFromLayer = t2;
  let t3;
  if ($[3] !== isMobileTabletViewport) {
    t3 = focused => {
      setIsFocused(focused);
      if (focused && isMobileTabletViewport) {
        setIsSearchLayerOpen(true);
      }
    };
    $[3] = isMobileTabletViewport;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const handleFocusChange = t3;
  const shouldHide = hidden && !isFocused;
  const spacerHeightClassName = trendKeyword ? "mobile-tablet:h-98" : "mobile-tablet:h-48";
  const t4 = shouldHide && "mobile-tablet:-translate-y-full";
  let t5;
  if ($[5] !== t4) {
    t5 = cn("flex w-full flex-col items-center gap-12 bg-Gray-white", "mobile-tablet:fixed mobile-tablet:inset-x-0 mobile-tablet:top-0 mobile-tablet:z-10 mobile-tablet:gap-0", "mobile-tablet:transition-transform mobile-tablet:duration-200", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== filterCount || $[8] !== handleFocusChange || $[9] !== keyword || $[10] !== onFilterClick || $[11] !== onSearch || $[12] !== scope) {
    t6 = <SearchBar keyword={keyword} filterCount={filterCount} sticky={false} scope={scope} onSearch={onSearch} onFilterClick={onFilterClick} onFocusChange={handleFocusChange} />;
    $[7] = filterCount;
    $[8] = handleFocusChange;
    $[9] = keyword;
    $[10] = onFilterClick;
    $[11] = onSearch;
    $[12] = scope;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== onClearRecent || $[15] !== onRemoveRecent || $[16] !== onSelectRecent || $[17] !== recentKeywords) {
    t7 = recentKeywords.length > 0 && <div className="hidden w-full xl:block"><RecentSearchChips keywords={recentKeywords} onSelect={onSelectRecent} onRemove={onRemoveRecent} onClearAll={onClearRecent} /></div>;
    $[14] = onClearRecent;
    $[15] = onRemoveRecent;
    $[16] = onSelectRecent;
    $[17] = recentKeywords;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== trendKeyword) {
    t8 = trendKeyword && <div className="hidden w-full mobile-tablet:block">{trendKeyword}</div>;
    $[19] = trendKeyword;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== spacerHeightClassName) {
    t10 = cn("hidden mobile-tablet:block", spacerHeightClassName);
    $[26] = spacerHeightClassName;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t10) {
    t11 = <div aria-hidden={true} className={t10} />;
    $[28] = t10;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== isMobileTabletViewport || $[31] !== isSearchLayerVisible || $[32] !== keyword || $[33] !== onClearRecent || $[34] !== onRemoveRecent || $[35] !== onSearch || $[36] !== onSelectRecent || $[37] !== recentKeywords || $[38] !== scope || $[39] !== searchFromLayer) {
    t12 = isMobileTabletViewport && <MobileSearchOverlay isOpen={isSearchLayerVisible} keyword={keyword} scope={scope} recentKeywords={recentKeywords} onClose={closeSearchLayer} onSearch={searchFromLayer(onSearch)} onSelectRecent={searchFromLayer(onSelectRecent)} onRemoveRecent={onRemoveRecent} onClearRecent={onClearRecent} />;
    $[30] = isMobileTabletViewport;
    $[31] = isSearchLayerVisible;
    $[32] = keyword;
    $[33] = onClearRecent;
    $[34] = onRemoveRecent;
    $[35] = onSearch;
    $[36] = onSelectRecent;
    $[37] = recentKeywords;
    $[38] = scope;
    $[39] = searchFromLayer;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== ref || $[42] !== t11 || $[43] !== t12 || $[44] !== t9) {
    t13 = <div ref={ref} className="w-full flex-1">{t9}{t11}{t12}</div>;
    $[41] = ref;
    $[42] = t11;
    $[43] = t12;
    $[44] = t9;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
};